import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { PACKAGE_PRICES } from '../../config/contracts';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errors';
import { formatUsdt } from '../../utils/format';
import MiniLoader from '../common/MiniLoader';

const RoyaltyCard = ({ pkgId, userId, refreshKey, onClaimed }) => {
  const { logic, storage } = useContracts();
  const notify = useNotify();

  const [pending, setPending] = useState(ethers.BigNumber.from(0));
  const [eligible, setEligible] = useState(false);
  const [eligibleCount, setEligibleCount] = useState(0);
  const [pkg1Directs, setPkg1Directs] = useState(0);
  const [claiming, setClaiming] = useState(false);
  const [loading, setLoading] = useState(false);
  const runIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!userId) return;
      const myRun = ++runIdRef.current;
      setLoading(true);
      try {
        // Eligibility rule: directsInPackage[user][1] >= 5 (same for all royalty tiers)
        const [p, elig, count, directs] = await Promise.all([
          logic.getPendingRoyalty(userId, pkgId),
          storage.royaltyEligibility(pkgId, userId),
          storage.royaltyEligibleCount(pkgId),
          storage.directsInPackage(userId, 1)
        ]);
        if (cancelled || myRun !== runIdRef.current) return;
        setPending(p);
        setEligible(elig);
        setEligibleCount(count.toNumber ? count.toNumber() : Number(count));
        setPkg1Directs(directs.toNumber ? directs.toNumber() : Number(directs));
      } catch (_) { /* ignore */ }
      finally {
        if (myRun === runIdRef.current) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [userId, pkgId, refreshKey]);

  const claim = async () => {
    if (pending.isZero()) return;
    setClaiming(true);
    const loadingId = notify.loading(`Claiming royalty from Package ${pkgId}...`);
    try {
      const tx = await logic.claimRoyalty(pkgId);
      notify.dismiss(loadingId);
      const mining = notify.loading('Waiting for confirmation...');
      await tx.wait();
      notify.dismiss(mining);
      notify.success(`Royalty claimed: ${formatUsdt(pending)} USDT`);
      if (onClaimed) onClaimed();
    } catch (err) {
      notify.dismiss(loadingId);
      notify.error(parseError(err));
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Package {pkgId}</div>
          <div className="card-subtitle">${PACKAGE_PRICES[pkgId - 1]} tier</div>
        </div>
        {eligible ? (
          <span className="badge badge-success">Eligible</span>
        ) : (
          <span className="badge badge-warning">Not Eligible</span>
        )}
      </div>

      <div className="grid grid-2 gap-md mb-md">
        <div className="stat">
          <span className="stat-label">Pending Reward</span>
          <span className="stat-value text-gradient">
            {loading ? '...' : formatUsdt(pending)}<span className="stat-unit">USDT</span>
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Total Eligible</span>
          <span className="stat-value">{eligibleCount}</span>
        </div>
      </div>

      {!eligible && (
        <div className="mb-md" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Need 5 direct P1 referrals (you have {pkg1Directs}) + Package {pkgId + 1} activated
        </div>
      )}

      <button
        className="btn btn-primary btn-full btn-sm"
        disabled={pending.isZero() || claiming}
        onClick={claim}
      >
        {claiming ? <><MiniLoader /> Claiming</> : pending.isZero() ? 'Nothing to Claim' : 'Claim Royalty'}
      </button>
    </div>
  );
};

export default RoyaltyCard;
